import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { formatDayRange } from './dateRangeFormat';
import type { TimeOffRequestDto } from '../../types';

interface CalendarRequestListItemProps {
  request: TimeOffRequestDto;
  typeName: string;
  onClick?: () => void;
}

const STATUS_COLOR: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  Approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  Cancelled: 'bg-muted text-muted-foreground',
};

/** "Mis solicitudes" row: date range + day count ("15 - 17 mar 2026 · 3 días"), not a comma-joined day list. */
export function CalendarRequestListItem({
  request,
  typeName,
  onClick,
}: CalendarRequestListItemProps) {
  const { t } = useTranslation('timeOff');
  const activeDays = (request.days ?? []).filter((day) => day.status !== 'Cancelled');
  const range = activeDays.length
    ? formatDayRange(
        activeDays.map((day) => day.date),
        { includeYear: true },
      )
    : '';

  return (
    <div
      className="cursor-pointer space-y-1 py-3 first:pt-0 last:pb-0"
      onClick={onClick}
      data-testid={`time-off-requests-card-${request.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{typeName}</span>
        <Badge className={STATUS_COLOR[request.status ?? '']}>
          {request.status ? t(`requests.status.${request.status}`) : ''}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {range}
        {activeDays.length > 0 && (
          <> · {t('requests.table.dayCount', { count: activeDays.length })}</>
        )}
      </p>
    </div>
  );
}
